/**
 * Subtle scroll-reveal: fades and gently raises sections, product cards, and
 * content cards into view once as the shopper scrolls to them. Purely
 * additive (no markup changes needed) - see the CSS rules gated behind
 * `.scroll-reveal-ready` in base.css for the actual animation.
 *
 * Tune the visual feel (distance/duration/easing/stagger) via the
 * `--scroll-reveal-*` custom properties in base.css. Tune selection/observer
 * behavior via the constants below.
 */

const GROUP_SELECTOR = '#MainContent .shopify-section';
const ITEM_SELECTOR = '.resource-card, .card';

/** Stagger delay stops increasing after this many items in a group, so long grids don't drag out. */
const MAX_STAGGER_ITEMS = 6;

const OBSERVER_OPTIONS = {
  root: null,
  // Trigger a little before an element's bottom edge reaches the viewport bottom.
  rootMargin: '0px 0px -10% 0px',
  threshold: 0.1,
};

const REVEALED_ATTRIBUTE = 'data-revealed';
const STAGGER_DELAY_PROPERTY = '--reveal-delay';

if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  // Leave the page fully static: no ready class means the CSS never hides anything.
} else {
  init();
}

function init() {
  document.documentElement.classList.add('scroll-reveal-ready');

  const observer = new IntersectionObserver(handleIntersect, OBSERVER_OPTIONS);
  const observedElements = new WeakSet();

  observeNewElements(document, observer, observedElements);

  const mainContent = document.getElementById('MainContent');
  if (mainContent) {
    new MutationObserver(() => observeNewElements(mainContent, observer, observedElements)).observe(mainContent, {
      childList: true,
      subtree: true,
    });
  }
}

/**
 * @param {ParentNode} root
 * @param {IntersectionObserver} observer
 * @param {WeakSet<Element>} observedElements
 */
function observeNewElements(root, observer, observedElements) {
  for (const group of root.querySelectorAll(GROUP_SELECTOR)) {
    registerElement(group, observer, observedElements);

    const items = group.querySelectorAll(ITEM_SELECTOR);
    items.forEach((item, index) => {
      const delayStep = Math.min(index, MAX_STAGGER_ITEMS);
      item.style.setProperty(STAGGER_DELAY_PROPERTY, `calc(var(--scroll-reveal-stagger-step) * ${delayStep})`);
      registerElement(item, observer, observedElements);
    });
  }
}

/**
 * @param {Element} element
 * @param {IntersectionObserver} observer
 * @param {WeakSet<Element>} observedElements
 */
function registerElement(element, observer, observedElements) {
  if (observedElements.has(element) || element.hasAttribute(REVEALED_ATTRIBUTE)) return;

  observedElements.add(element);

  // Already in (or past) the viewport when we first see it: reveal instantly, no animation.
  if (element.getBoundingClientRect().top < window.innerHeight) {
    // Non-empty value: morph.js's PRESERVED_ATTRIBUTES restore only fires for a truthy old value.
    element.setAttribute(REVEALED_ATTRIBUTE, 'true');
    return;
  }

  observer.observe(element);
}

/** @param {IntersectionObserverEntry[]} entries @param {IntersectionObserver} observer */
function handleIntersect(entries, observer) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    entry.target.setAttribute(REVEALED_ATTRIBUTE, 'true');
    observer.unobserve(entry.target);
  }
}
